import type { NoiseReductionAudioProcessingConstraints, NoiseReductionProvider } from "./NoiseReductionPolicy";
import { getAudioProcessingConstraints } from "./NoiseReductionPolicy";
import {
    DEFAULT_MICROPHONE_GAIN_MODE,
    type MicrophoneGainMode,
    type MicrophoneGainStatus,
} from "./MicrophoneGainConfig";

interface AutoGainControlSupportLike {
    autoGainControl?: boolean;
}

interface AudioConstraintSupportLike extends AutoGainControlSupportLike {
    deviceId?: boolean;
}

export interface AudioCapturePolicyResult {
    constraints: NoiseReductionAudioProcessingConstraints;
    effectiveMicrophoneGainMode: MicrophoneGainMode;
    status: Extract<MicrophoneGainStatus, "active" | "unavailable">;
    fallbackReason: string | null;
}

export interface AudioConstraintResult extends Omit<AudioCapturePolicyResult, "constraints"> {
    constraints: MediaTrackConstraints;
}

export const AUTOMATIC_MICROPHONE_GAIN_UNAVAILABLE_REASON =
    "Automatic microphone gain control is not supported by this browser.";
export const AUTOMATIC_MICROPHONE_GAIN_WITH_RNNOISE_UNAVAILABLE_REASON =
    "Automatic microphone gain control is not available with RNNoise yet.";

export function isAutoGainControlSupported(
    supportedConstraints: AutoGainControlSupportLike =
        typeof navigator !== "undefined" && navigator.mediaDevices ? navigator.mediaDevices.getSupportedConstraints() : {}
): boolean {
    return supportedConstraints.autoGainControl === true;
}

export function getAudioCapturePolicy(
    provider: NoiseReductionProvider,
    microphoneGainMode: MicrophoneGainMode,
    supportedConstraints?: AutoGainControlSupportLike
): AudioCapturePolicyResult {
    const baseConstraints = getAudioProcessingConstraints(provider);

    switch (microphoneGainMode) {
        case "provider-default":
            return {
                constraints: baseConstraints,
                effectiveMicrophoneGainMode: "provider-default",
                status: "active",
                fallbackReason: null,
            };
        case "manual":
            return {
                constraints: {
                    ...baseConstraints,
                    autoGainControl: false,
                },
                effectiveMicrophoneGainMode: "manual",
                status: "active",
                fallbackReason: null,
            };
        case "automatic":
            if (provider === "rnnoise") {
                return {
                    constraints: baseConstraints,
                    effectiveMicrophoneGainMode: DEFAULT_MICROPHONE_GAIN_MODE,
                    status: "unavailable",
                    fallbackReason: AUTOMATIC_MICROPHONE_GAIN_WITH_RNNOISE_UNAVAILABLE_REASON,
                };
            }

            if (!isAutoGainControlSupported(supportedConstraints)) {
                return {
                    constraints: baseConstraints,
                    effectiveMicrophoneGainMode: DEFAULT_MICROPHONE_GAIN_MODE,
                    status: "unavailable",
                    fallbackReason: AUTOMATIC_MICROPHONE_GAIN_UNAVAILABLE_REASON,
                };
            }

            return {
                constraints: {
                    ...baseConstraints,
                    autoGainControl: true,
                },
                effectiveMicrophoneGainMode: "automatic",
                status: "active",
                fallbackReason: null,
            };
    }
}

export function buildAudioConstraints(
    microphoneDeviceId: string | undefined,
    provider: NoiseReductionProvider,
    microphoneGainMode: MicrophoneGainMode,
    supportedConstraints: AudioConstraintSupportLike =
        typeof navigator !== "undefined" && navigator.mediaDevices ? navigator.mediaDevices.getSupportedConstraints() : {}
): AudioConstraintResult {
    const capturePolicy = getAudioCapturePolicy(provider, microphoneGainMode, supportedConstraints);
    const constraints = {
        ...(typeof capturePolicy.constraints === "boolean" ? {} : capturePolicy.constraints),
    } as MediaTrackConstraints;

    if (microphoneDeviceId !== undefined && supportedConstraints.deviceId === true) {
        constraints.deviceId = { exact: microphoneDeviceId };
    }

    return {
        ...capturePolicy,
        constraints,
    };
}
